import { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Typography,
  Paper,
  Container,
  Button,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableContainer,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  TextField,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Checkbox,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
  Tooltip,
} from '@mui/material'
import {
  Shuffle,
  CheckCircle,
  Upload,
  Delete,
  Download,
  AssignmentInd,
  Warning,
  Schedule,
} from '@mui/icons-material'
import { useAppSelector } from '../hooks/redux'
import { hasPermission } from '../utils/permissions'
import { useGetCasesQuery } from '../store/api/casesApi'
import { useGetTeamsQuery } from '../store/api/teamsApi'
import { useGetRoundsQuery } from '../store/api/roundsApi'
import {
  useDrawBracketMutation,
  useGetLatestBracketQuery,
  useConfirmBracketMutation,
  useListRoleSheetsQuery,
  useUploadRoleSheetMutation,
  useDeleteRoleSheetMutation,
  useLazyDownloadRoleSheetQuery,
  useSubmitStructuredRoleSheetMutation,
  useGetTeamRoundSheetQuery,
  type RoleEntry,
  type RolePosition,
} from '../store/api/bracketsApi'
import { formatCaseDisplay } from '../utils/caseDisplay'
import { getBracketDrawBlockedReason } from '../utils/r2'
import { PageHeader } from '../components/PageHeader'
import { getContentPaperSx, getFilterPaperSx, getTablePaperSx } from '../theme/sectionSurfaces'

export default function BracketsPage() {
  const { user } = useAppSelector((s) => s.auth)
  const canManage = user && hasPermission(user.role, 'ASSIGN_CASES')
  const { data: cases = [] } = useGetCasesQuery()
  const { data: teams = [] } = useGetTeamsQuery()
  const { data: rounds = [] } = useGetRoundsQuery()
  const [caseId, setCaseId] = useState('')
  // UAT-Bán kết #1: chọn round + đội đủ điều kiện
  const [roundId, setRoundId] = useState<string>('')
  const [eligibleTeams, setEligibleTeams] = useState<string[]>([])

  useEffect(() => {
    if (cases.length && !caseId) setCaseId(cases[0].id)
  }, [cases, caseId])

  const { data: bracket, refetch: refetchBracket } = useGetLatestBracketQuery(caseId, {
    skip: !caseId,
  })
  const { data: sheets = [], refetch: refetchSheets } = useListRoleSheetsQuery(caseId, {
    skip: !caseId,
  })

  const [drawBracket, { isLoading: drawing }] = useDrawBracketMutation()
  const [confirmBracket, { isLoading: confirming }] = useConfirmBracketMutation()
  const [uploadSheet, { isLoading: uploading }] = useUploadRoleSheetMutation()
  const [deleteSheet] = useDeleteRoleSheetMutation()
  const [downloadSheet] = useLazyDownloadRoleSheetQuery()
  const [sheetTitle, setSheetTitle] = useState('')

  const handleDraw = async () => {
    if (!caseId) return
    try {
      await drawBracket({
        case_id: caseId,
        round_id: roundId || undefined,
        eligible_team_ids: eligibleTeams.length > 0 ? eligibleTeams : undefined,
      }).unwrap()
      refetchBracket()
    } catch (e: any) {
      alert(e?.data?.detail || e?.message || 'Bốc thăm thất bại')
    }
  }

  // Lọc rounds + teams thuộc case đang chọn
  const caseRounds = rounds.filter((r: any) => r.case_id === caseId)
  const caseObj = cases.find((c) => c.id === caseId)
  const allCaseTeams = teams.filter((t) => caseObj?.assigned_team_ids?.includes(t.id))

  // R1→R2 doc Bước 5: M05 chỉ áp dụng cho đội đã advance (Top 4 từ Sơ loại)
  const selectedRoundType = (caseRounds.find((r) => r.id === roundId) as any)?.round_type
  const isHearingRound = selectedRoundType === 'semifinal' || selectedRoundType === 'final'
  const advancedTeams = allCaseTeams.filter((t: any) =>
    isHearingRound
      ? t.competition_status === 'advanced_to_semifinal' ||
        t.competition_status === 'advanced_to_final'
      : true,
  )
  const eliminatedTeams = allCaseTeams.filter((t: any) => t.competition_status === 'eliminated')
  // Khi đang chọn vòng Bán kết/Chung kết → restrict eligibleTeams chỉ Top 4
  const assignedTeams = isHearingRound ? advancedTeams : allCaseTeams
  const currentEligibleCount = eligibleTeams.length || assignedTeams.length
  // BE enforce: semifinal=4, final=2 — chặn nút ngay từ FE để UX tốt hơn.
  // Logic ở utils/r2.ts để Vitest test được mà không cần render component.
  const drawBlockedReason = getBracketDrawBlockedReason({
    caseId,
    roundType: selectedRoundType,
    eligibleCount: currentEligibleCount,
  })
  const bracketStats = [
    { label: 'Đội trong cuộc thi', value: allCaseTeams.length, color: 'primary' as const },
    { label: 'Đội đủ điều kiện', value: currentEligibleCount, color: 'success' as const },
    { label: 'Cặp đấu', value: bracket?.matches?.length || 0, color: 'secondary' as const },
    { label: 'Bảng phân vai', value: sheets.length, color: 'warning' as const },
  ]

  const handleConfirm = async () => {
    if (!bracket?.id) return
    if (!window.confirm('Xác nhận bracket? Sau khi xác nhận không thể chỉnh sửa.')) return
    try {
      await confirmBracket(bracket.id as string).unwrap()
      refetchBracket()
    } catch (e: any) {
      alert(e?.data?.detail || e?.message || 'Xác nhận thất bại')
    }
  }

  const handleUpload = async (file: File | null) => {
    if (!file || !caseId) return
    try {
      await uploadSheet({ caseId, title: sheetTitle || undefined, file }).unwrap()
      setSheetTitle('')
      refetchSheets()
    } catch (e: any) {
      alert(e?.data?.detail || e?.message || 'Upload thất bại')
    }
  }

  // ── Structured submission (M05-2): mở dialog 4 vai ─────────────────────
  const [structOpen, setStructOpen] = useState(false)

  if (!canManage) {
    return (
      <Container maxWidth="xl" sx={{ mt: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 4 }, px: { xs: 2, sm: 3 } }}>
        <PageHeader
          title="Bốc thăm & bảng phân vai"
          subtitle="M05 là màn điều phối dành cho Ban Thư ký/BTC."
          icon={<Shuffle />}
        />
        <Alert severity="info">
          Bạn không có quyền điều phối bốc thăm. Sinh viên nộp bảng phân vai trong module Đơn khởi kiện theo cuộc thi được phân.
        </Alert>
      </Container>
    )
  }

  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 4 }, px: { xs: 2, sm: 3 } }}>
      <PageHeader
        title="Bốc thăm & bảng phân vai"
        subtitle="M05: chọn cuộc thi, bốc thăm cặp Nguyên đơn / Bị đơn và theo dõi bảng phân vai."
        icon={<Shuffle />}
      />

      <Paper
        elevation={0}
        sx={(theme) => ({
          ...getFilterPaperSx(theme),
          display: 'grid',
          gap: 2,
          gridTemplateColumns: {
            xs: '1fr',
            md: 'minmax(260px, 1.2fr) minmax(220px, 0.9fr)',
            lg: 'minmax(320px, 1.25fr) minmax(240px, 0.9fr) minmax(360px, 1.35fr)',
          },
          alignItems: 'start',
        })}
      >
        <FormControl fullWidth>
          <InputLabel>Cuộc thi</InputLabel>
          <Select
            label="Cuộc thi"
            value={caseId}
            onChange={(e) => {
              setCaseId(e.target.value as string)
              setRoundId('')
              setEligibleTeams([])
            }}
          >
            {cases.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {formatCaseDisplay(c)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {/* UAT-Bán kết #1: chọn vòng cụ thể */}
        <FormControl fullWidth disabled={!caseId}>
          <InputLabel>Vòng (tuỳ chọn)</InputLabel>
          <Select
            label="Vòng (tuỳ chọn)"
            value={roundId}
            onChange={(e) => setRoundId(e.target.value as string)}
          >
            <MenuItem value=""><em>— Không gắn vòng —</em></MenuItem>
            {caseRounds.map((r: any) => (
              <MenuItem key={r.id} value={r.id}>
                {r.name} ({r.round_type})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {/* UAT-Bán kết #1: multi-select đội đủ điều kiện cho vòng (VD: 4 đội qua sơ loại) */}
        <FormControl fullWidth disabled={!caseId}>
          <InputLabel>Đội đủ điều kiện (để trống = toàn bộ đội của vụ)</InputLabel>
          <Select
            multiple
            label="Đội đủ điều kiện (để trống = toàn bộ đội của vụ)"
            value={eligibleTeams}
            onChange={(e) => setEligibleTeams(e.target.value as string[])}
            renderValue={(sel) =>
              (sel as string[]).map((id) => teams.find((t) => t.id === id)?.name || id).join(', ') ||
              '(toàn bộ đội)'
            }
          >
            {assignedTeams.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                <ListItemIcon>
                  <Checkbox checked={eligibleTeams.indexOf(t.id) > -1} edge="start" />
                </ListItemIcon>
                <ListItemText primary={t.name} secondary={t.university} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {eligibleTeams.length > 0 && eligibleTeams.length % 2 !== 0 && (
          <Alert severity="warning" sx={{ gridColumn: '1 / -1' }}>
            Số đội đủ điều kiện ({eligibleTeams.length}) không chẵn — hệ thống yêu cầu chẵn để ghép cặp.
          </Alert>
        )}
        {/* R1→R2 doc Bước 5: M05 chỉ cho Top 4 đội đã advance */}
        {isHearingRound && advancedTeams.length === 0 && (
          <Alert severity="warning" icon={<Warning />} sx={{ gridColumn: '1 / -1' }}>
            Vòng này chưa có đội nào được công bố vào Bán kết/Chung kết. Hãy hoàn tất công bố
            kết quả vòng trước (ReportsPage) để mở M05 cho Top 4.
          </Alert>
        )}
        {isHearingRound && advancedTeams.length > 0 && (
          <Alert severity="success" icon={<CheckCircle />} sx={{ gridColumn: '1 / -1' }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <span>
                <b>{advancedTeams.length} đội</b> đã được công bố vào vòng này:
              </span>
              {advancedTeams.map((t) => (
                <Chip
                  key={t.id}
                  size="small"
                  label={t.name}
                  color="success"
                  variant="outlined"
                />
              ))}
              {eliminatedTeams.length > 0 && (
                <Tooltip title={eliminatedTeams.map((t) => t.name).join(', ')}>
                  <Chip
                    size="small"
                    label={`${eliminatedTeams.length} đội đã loại`}
                    color="default"
                  />
                </Tooltip>
              )}
            </Stack>
          </Alert>
        )}
      </Paper>

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, minmax(0, 1fr))',
            lg: 'repeat(4, minmax(0, 1fr))',
          },
          mb: 3,
        }}
      >
        {bracketStats.map((item) => (
          <Paper
            key={item.label}
            elevation={0}
            sx={(theme) => ({
              p: 2,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              minHeight: 86,
            })}
          >
            <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h6" fontWeight={800} lineHeight={1.1}>
                  {item.value}
                </Typography>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  {item.label}
                </Typography>
              </Box>
              <Chip size="small" color={item.color} label="M05" variant="outlined" />
            </Stack>
          </Paper>
        ))}
      </Box>

      <Paper elevation={0} sx={(theme) => ({ ...getContentPaperSx(theme), p: 3, mb: 3 })}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', md: 'center' }}
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="h6" fontWeight={800}>
              Bracket
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Ghép cặp tranh tụng và khóa kết quả bốc thăm cho vòng đã chọn.
            </Typography>
          </Box>
          {canManage && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="flex-end">
              <Tooltip title={drawBlockedReason || ''} disableHoverListener={!drawBlockedReason}>
                <span>
                  <Button
                    variant="contained"
                    startIcon={<Shuffle />}
                    onClick={handleDraw}
                    disabled={drawing || !!drawBlockedReason}
                    sx={{ textTransform: 'none', fontWeight: 700 }}
                  >
                    Bốc thăm lại
                  </Button>
                </span>
              </Tooltip>
              <Button
                variant="outlined"
                color="success"
                startIcon={<CheckCircle />}
                onClick={handleConfirm}
                disabled={confirming || !bracket?.id || bracket.status === 'confirmed' || !bracket.id}
                sx={{ textTransform: 'none', fontWeight: 700 }}
              >
                Xác nhận bracket
              </Button>
            </Stack>
          )}
        </Stack>
        {!canManage && (
          <Alert severity="info">Chỉ BTC (quyền phân công cuộc thi) mới bốc thăm / xác nhận.</Alert>
        )}
        {!bracket && <Alert severity="warning">Chưa có bracket cho cuộc thi này.</Alert>}
        {bracket && (
          <>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              <Chip
                label={bracket.status === 'confirmed' ? 'Đã khóa (confirmed)' : 'Bản nháp'}
                color={bracket.status === 'confirmed' ? 'success' : 'default'}
              />
              {bracket.round_type && (
                <Chip
                  label={`Vòng: ${bracket.round_type}`}
                  color="primary"
                  variant="outlined"
                />
              )}
              {bracket.eligible_team_ids && bracket.eligible_team_ids.length > 0 && (
                <Chip
                  label={`${bracket.eligible_team_ids.length} đội đủ điều kiện`}
                  variant="outlined"
                />
              )}
            </Box>
            <TableContainer component={Paper} elevation={0} sx={(theme) => getTablePaperSx(theme)}>
              <Table size="small" sx={{ minWidth: 620 }}>
                <TableHead>
                  <TableRow sx={{ '& th': { bgcolor: 'background.default', whiteSpace: 'nowrap' } }}>
                    <TableCell sx={{ width: 72 }}>#</TableCell>
                    <TableCell>Nguyên đơn</TableCell>
                    <TableCell>Bị đơn</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bracket.matches.map((m, idx) => {
                    const tp = teams.find((t) => t.id === m.team_plaintiff_id)
                    const td = teams.find((t) => t.id === m.team_defendant_id)
                    return (
                      <TableRow key={idx} hover>
                        <TableCell>
                          <Chip size="small" label={`Cặp ${idx + 1}`} variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={700}>
                            {tp?.name || '— Đội chưa rõ —'}
                          </Typography>
                          {tp?.university && (
                            <Typography variant="caption" color="text.secondary">
                              {tp.university}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={700}>
                            {td?.name || '— Đội chưa rõ —'}
                          </Typography>
                          {td?.university && (
                            <Typography variant="caption" color="text.secondary">
                              {td.university}
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Paper>

      <Paper elevation={0} sx={(theme) => ({ ...getContentPaperSx(theme), p: 3 })}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', md: 'center' }}
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="h6" fontWeight={800}>
              Bảng phân vai
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Đội nộp 4 vai cho vòng đã chọn; BTC có thể upload file PDF bổ sung.
            </Typography>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
          {/* Structured form — đội nộp 4 vai online */}
          <Button
            variant="contained"
            startIcon={<AssignmentInd />}
            disabled={!caseId || !roundId}
            onClick={() => setStructOpen(true)}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Nộp Bảng phân vai (4 vai)
          </Button>
          {canManage && (
            <>
              <TextField
                label="Tiêu đề (tuỳ chọn)"
                value={sheetTitle}
                onChange={(e) => setSheetTitle(e.target.value)}
                size="small"
                sx={{ minWidth: { sm: 220 } }}
              />
              <Button
                variant="outlined"
                component="label"
                startIcon={<Upload />}
                disabled={uploading || !caseId}
                sx={{ textTransform: 'none', fontWeight: 700 }}
              >
                Upload PDF (BTC)
                <input
                  type="file"
                  hidden
                  accept=".pdf,application/pdf"
                  onChange={(e) => {
                    const f = e.target.files?.[0]
                    e.target.value = ''
                    void handleUpload(f || null)
                  }}
                />
              </Button>
            </>
          )}
          </Stack>
        </Stack>
        {!roundId && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Chọn vòng ở khung lọc phía trên trước khi nộp bảng phân vai.
          </Alert>
        )}
        <List
          dense
          disablePadding
          sx={{
            display: 'grid',
            gap: 1.5,
          }}
        >
          {sheets.length === 0 && (
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Chưa có bảng phân vai.
              </Typography>
            </Paper>
          )}
          {sheets.map((s) => (
            <ListItem
              key={s.id}
              sx={(theme) => ({
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                px: 2,
                py: 1.5,
                bgcolor: 'background.paper',
                alignItems: 'flex-start',
                '& .MuiListItemSecondaryAction-root': {
                  right: 12,
                  top: 12,
                  transform: 'none',
                },
              })}
              secondaryAction={
                <Stack direction="row" spacing={0.5}>
                  {/* Structured sheets không có file đính kèm → ẩn nút download. */}
                  {s.submission_kind !== 'structured' && s.storage_path && (
                    <IconButton
                      edge="end"
                      aria-label="download"
                      onClick={async () => {
                        try {
                          const blob = await downloadSheet(s.id!).unwrap()
                          const url = window.URL.createObjectURL(blob)
                          const a = document.createElement('a')
                          a.href = url
                          a.download = s.file_name || 'role-sheet.pdf'
                          document.body.appendChild(a)
                          a.click()
                          window.URL.revokeObjectURL(url)
                          document.body.removeChild(a)
                        } catch (e: any) {
                          alert(e?.data?.detail || e?.message || 'Tải thất bại')
                        }
                      }}
                    >
                      <Download />
                    </IconButton>
                  )}
                  {canManage && (
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      onClick={async () => {
                        if (!window.confirm('Xóa bảng phân vai?')) return
                        try {
                          await deleteSheet(s.id!).unwrap()
                          refetchSheets()
                        } catch (e: any) {
                          alert(e?.data?.detail || e?.message)
                        }
                      }}
                    >
                      <Delete />
                    </IconButton>
                  )}
                </Stack>
              }
            >
              <ListItemText
                primary={
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <span>{s.title}</span>
                    {s.representing_role && (
                      <Chip
                        size="small"
                        label={s.representing_role === 'plaintiff' ? 'Nguyên đơn' : 'Bị đơn'}
                        color={s.representing_role === 'plaintiff' ? 'primary' : 'secondary'}
                      />
                    )}
                    {s.is_late === true && (
                      <Tooltip title={`Nộp muộn ${s.minutes_late || 0} phút — bị trừ 20đ`}>
                        <Chip size="small" color="warning" icon={<Warning />} label="Muộn −20đ" />
                      </Tooltip>
                    )}
                    {s.is_late === false && (
                      <Chip size="small" color="success" icon={<CheckCircle />} label="Đúng hạn" />
                    )}
                  </Stack>
                }
                secondary={
                  s.roles && s.roles.length > 0 ? (
                    <Box sx={{ mt: 0.5 }}>
                      {s.roles.map((r) => (
                        <Typography key={r.position} variant="caption" display="block">
                          • {ROLE_LABELS[r.position]}: <b>{r.full_name}</b>
                          {r.student_id ? ` (MSSV ${r.student_id})` : ''}
                        </Typography>
                      ))}
                    </Box>
                  ) : (
                    s.file_name
                  )
                }
              />
            </ListItem>
          ))}
        </List>
      </Paper>

      {/* Structured submission dialog */}
      <StructuredRoleSheetDialog
        open={structOpen}
        onClose={() => setStructOpen(false)}
        caseId={caseId}
        roundId={roundId}
        teams={assignedTeams}
        onSubmitted={() => {
          setStructOpen(false)
          refetchSheets()
        }}
      />
    </Container>
  )
}

// ════════════════════════════════════════════════════════════════════
// Structured Role Sheet Dialog (M05-2 Vòng Bán kết)
// ════════════════════════════════════════════════════════════════════

const ROLE_LABELS: Record<RolePosition, string> = {
  party: 'Đương sự',
  lawyer_lead: 'Người bảo vệ chính (Trưởng nhóm)',
  lawyer_support_1: 'Người bảo vệ hỗ trợ 1',
  lawyer_support_2: 'Người bảo vệ hỗ trợ 2',
}

const ROLE_ORDER: RolePosition[] = ['party', 'lawyer_lead', 'lawyer_support_1', 'lawyer_support_2']

function emptyRoles(): RoleEntry[] {
  return ROLE_ORDER.map((p) => ({ position: p, full_name: '', student_id: '' }))
}

function formatRemaining(ms: number): string {
  if (ms <= 0) return 'Đã hết hạn'
  const totalMin = Math.floor(ms / 60000)
  const days = Math.floor(totalMin / 1440)
  const hours = Math.floor((totalMin % 1440) / 60)
  const mins = totalMin % 60
  if (days > 0) return `${days} ngày ${hours} giờ`
  if (hours > 0) return `${hours} giờ ${mins} phút`
  return `${mins} phút`
}

function StructuredRoleSheetDialog({
  open,
  onClose,
  caseId,
  roundId,
  teams,
  onSubmitted,
}: {
  open: boolean
  onClose: () => void
  caseId: string
  roundId: string
  teams: any[]
  onSubmitted: () => void
}) {
  const [teamId, setTeamId] = useState('')
  const [representing, setRepresenting] = useState<'plaintiff' | 'defendant'>('plaintiff')
  const [roles, setRoles] = useState<RoleEntry[]>(emptyRoles())
  const [submitErr, setSubmitErr] = useState<string | null>(null)

  const [submitStructured, { isLoading: submitting }] = useSubmitStructuredRoleSheetMutation()

  // Auto-pick team for non-BTC users
  useEffect(() => {
    if (!open) return
    if (!teamId && teams.length === 1) setTeamId(teams[0].id)
  }, [open, teams, teamId])

  // Reset form when opening
  useEffect(() => {
    if (open) {
      setRoles(emptyRoles())
      setSubmitErr(null)
    }
  }, [open])

  const { data: roundSheet } = useGetTeamRoundSheetQuery(
    { case_id: caseId, team_id: teamId, round_id: roundId },
    { skip: !open || !caseId || !teamId || !roundId },
  )

  // Live countdown
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!open) return
    const id = setInterval(() => setNow(Date.now()), 30_000)
    return () => clearInterval(id)
  }, [open])

  const deadlineMs = useMemo(() => {
    if (!roundSheet?.deadline_iso) return null
    return new Date(roundSheet.deadline_iso).getTime()
  }, [roundSheet?.deadline_iso])

  const remainingMs = deadlineMs ? deadlineMs - now : null
  const willBeLate = remainingMs !== null && remainingMs <= 0

  const updateRole = (pos: RolePosition, patch: Partial<RoleEntry>) => {
    setRoles((prev) => prev.map((r) => (r.position === pos ? { ...r, ...patch } : r)))
  }

  const handleSubmit = async () => {
    setSubmitErr(null)
    if (!teamId) {
      setSubmitErr('Chọn đội thi.')
      return
    }
    // Validate full_name
    for (const r of roles) {
      if (!r.full_name.trim()) {
        setSubmitErr(`Họ tên cho vai "${ROLE_LABELS[r.position]}" không được trống.`)
        return
      }
    }
    try {
      await submitStructured({
        case_id: caseId,
        round_id: roundId,
        team_id: teamId,
        representing_role: representing,
        roles: roles.map((r) => ({
          ...r,
          full_name: r.full_name.trim(),
          student_id: r.student_id?.trim() || null,
        })),
      }).unwrap()
      onSubmitted()
    } catch (e: any) {
      setSubmitErr(e?.data?.detail || e?.message || 'Nộp thất bại')
    }
  }

  const existing = roundSheet?.sheet
  const canEdit = !existing  // 1 sheet/round/team — đã nộp thì readonly (BTC vẫn có thể delete + nộp lại)

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <AssignmentInd color="primary" />
          <span>Bảng phân vai — Vòng Bán kết</span>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {/* Team picker */}
          <FormControl size="small" fullWidth>
            <InputLabel>Đội thi</InputLabel>
            <Select
              value={teamId}
              label="Đội thi"
              onChange={(e) => setTeamId(e.target.value)}
              disabled={!!existing}
            >
              {teams.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Deadline countdown */}
          {deadlineMs && (
            <Alert
              severity={willBeLate ? 'error' : remainingMs! < 24 * 3600_000 ? 'warning' : 'info'}
              icon={<Schedule />}
            >
              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                <span>
                  <b>Hạn nộp (T-48h):</b>{' '}
                  {new Date(deadlineMs).toLocaleString('vi-VN')}
                </span>
                <Chip
                  size="small"
                  color={willBeLate ? 'error' : 'default'}
                  label={`Còn lại: ${formatRemaining(remainingMs!)}`}
                />
                {willBeLate && (
                  <Chip size="small" color="warning" icon={<Warning />} label="Sẽ bị trừ −20đ" />
                )}
              </Stack>
            </Alert>
          )}

          {/* Existing sheet readout */}
          {existing && (
            <Alert severity={existing.is_late ? 'warning' : 'success'}>
              Đội đã nộp Bảng phân vai
              {existing.is_late
                ? ` MUỘN ${existing.minutes_late || 0} phút (−20đ).`
                : ' đúng hạn.'}{' '}
              Liên hệ BTC để chỉnh sửa.
            </Alert>
          )}

          {/* Representing role */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Phía đại diện (theo bracket)
            </Typography>
            <RadioGroup
              row
              value={existing?.representing_role || representing}
              onChange={(e) => setRepresenting(e.target.value as 'plaintiff' | 'defendant')}
            >
              <FormControlLabel
                value="plaintiff"
                control={<Radio />}
                label="Nguyên đơn"
                disabled={!canEdit}
              />
              <FormControlLabel
                value="defendant"
                control={<Radio />}
                label="Bị đơn"
                disabled={!canEdit}
              />
            </RadioGroup>
          </Box>

          <Divider />

          {/* 4 role inputs */}
          <Typography variant="subtitle2">4 vai trong cặp đấu</Typography>
          <Box sx={{ display: 'grid', gap: 1.5 }}>
            {(existing?.roles || roles).map((r) => (
            <Paper key={r.position} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="body2" color="primary" fontWeight={800} gutterBottom>
                {ROLE_LABELS[r.position]}
              </Typography>
              <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) 180px' } }}>
                <TextField
                  label="Họ và tên"
                  value={r.full_name}
                  onChange={(e) =>
                    updateRole(r.position as RolePosition, { full_name: e.target.value })
                  }
                  size="small"
                  fullWidth
                  required
                  disabled={!canEdit}
                />
                <TextField
                  label="MSSV"
                  value={r.student_id || ''}
                  onChange={(e) =>
                    updateRole(r.position as RolePosition, { student_id: e.target.value })
                  }
                  size="small"
                  disabled={!canEdit}
                />
              </Box>
            </Paper>
            ))}
          </Box>

          {submitErr && <Alert severity="error">{submitErr}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Đóng</Button>
        {canEdit && (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting || !teamId}
            startIcon={<CheckCircle />}
          >
            {submitting ? 'Đang nộp…' : 'Nộp bảng phân vai'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
